import * as React from "react";
import { Alert, Link, Stack } from "@mui/material";
import { DataGridPremium, GridColDef, GridRenderCellParams, GridValueGetterParams } from "@mui/x-data-grid-premium";
import CclDataGridToolbar from "../../components/common/cclDataGridToolbar/cclDataGridToolbar";
import CclCommonLink from "../../components/common/cclCommonLink";
import { InventoryOrderDetail } from "../../services/types/assessmentAPITypes";

export interface OrderDetailDataGridProps {
    rows: InventoryOrderDetail[];
    isLoading: boolean;
}

const formatDate = (val: string | Date | null | undefined): string => {
    if (!val) return "";
    const d = val instanceof Date ? val : new Date(val as string);
    return isNaN(d.getTime()) ? "" : d.toLocaleDateString();
};

const COLUMNS: GridColDef[] = [
    {
        field: "programTitle",
        headerName: "Session Title",
        headerAlign: "left",
        align: "left",
        flex: 2,
        renderCell: (params: GridRenderCellParams) => (
            <CclCommonLink
                text={params.value}
                keyVal={String(params.row.esKey)}
                to={`/session/${params.row.esKey}`}
            />
        ),
    },
    {
        field: "startDate",
        headerName: "Session Date",

        headerAlign: "left",
        align: "left",
        flex: 1,
        type: "date",
        valueGetter: (params: GridValueGetterParams) =>
            params.value ? new Date(params.value) : null,
        renderCell: (params: GridRenderCellParams) => formatDate(params.row.startDate),
    },
    {
        field: "registrationStatus",
        headerName: "Status",
        headerAlign: "left",
        align: "left",
        flex: 1,
    },
    {
        field: "participantName",
        headerName: "Participant",
        headerAlign: "left",
        align: "left",
        flex: 1.5,
        renderCell: (params: GridRenderCellParams) => (
            <CclCommonLink
                text={params.value}
                keyVal={String(params.row.participantEsiKey)}
                to={`/registration/${params.row.participantEsiKey}`}
            />
        ),
    },
    {
        field: "participantEmail",
        headerName: "Email",
        headerAlign: "left",
        align: "left",
        flex: 1.5,
        renderCell: (params: GridRenderCellParams) => (
            <Link href={`mailto:${params.value}`}>{params.value}</Link>
        ),
    },
    {
        field: "registrationDate",
        headerName: "Registered",

        headerAlign: "left",
        align: "left",
        flex: 1,
        type: "date",
        valueGetter: (params: GridValueGetterParams) =>
            params.value ? new Date(params.value) : null,
        renderCell: (params: GridRenderCellParams) => formatDate(params.row.registrationDate),
    },
    {
        field: "language",
        headerName: "Language",
        headerAlign: "left",
        align: "left",
        flex: 0.75,
    },
    {
        field: "invitationScheduledDate",
        headerName: "Invitation (Scheduled)",

        headerAlign: "left",
        align: "left",
        flex: 1,
        type: "date",
        valueGetter: (params: GridValueGetterParams) =>
            params.value ? new Date(params.value) : null,
        renderCell: (params: GridRenderCellParams) => formatDate(params.row.invitationScheduledDate),
    },
    {
        field: "invitationSentDate",
        headerName: "Invitation (Actual)",

        headerAlign: "left",
        align: "left",
        flex: 1,
        type: "date",
        valueGetter: (params: GridValueGetterParams) =>
            params.value ? new Date(params.value) : null,
        renderCell: (params: GridRenderCellParams) => formatDate(params.row.invitationSentDate),
    },
    {
        field: "assessmentCompletionDate",
        headerName: "Assessment Completion",

        headerAlign: "left",
        align: "left",
        flex: 1,
        type: "date",
        valueGetter: (params: GridValueGetterParams) =>
            params.value ? new Date(params.value) : null,
        renderCell: (params: GridRenderCellParams) => formatDate(params.row.assessmentCompletionDate),
    },
    {
        field: "accessExpirationDate",
        headerName: "Access Expiration",

        headerAlign: "left",
        align: "left",
        flex: 1,
        type: "date",
        valueGetter: (params: GridValueGetterParams) =>
            params.value ? new Date(params.value) : null,
        renderCell: (params: GridRenderCellParams) => formatDate(params.row.accessExpirationDate),
    },
];

const OrderDetailDataGrid: React.FC<OrderDetailDataGridProps> = (props) => {
    if (!props.isLoading && props.rows.length === 0) {
        return (
            <Stack width={1} spacing={2} sx={{ p: 2 }}>
                <Alert variant="outlined" severity="info">
                    No registration information found for this order.
                </Alert>
            </Stack>
        );
    }

    return (
        <DataGridPremium
            getRowId={(row) => row.participantEsiKey}
            rows={props.rows}
            columns={COLUMNS}
            loading={props.isLoading}
            disableSelectionOnClick
            sx={{ flex: 1 }}
            initialState={{
                sorting: {
                    sortModel: [{ field: "registrationDate", sort: "desc" }],
                },
            }}
            components={{ Toolbar: CclDataGridToolbar }}
            componentsProps={{
                toolbar: {
                    showSaveState: false,
                    hideSettingsButtons: true,
                },
            }}
        />
    );
};

export default OrderDetailDataGrid;
